import type {
  ApolloClient,
  DefaultContext,
  DocumentNode,
  ErrorPolicy,
  OperationVariables,
  RefetchOn,
  RefetchWritePolicy,
  TypedDocumentNode,
  WatchQueryFetchPolicy,
} from "@apollo/client";
import type { PreloadedQueryRef } from "@apollo/client/react";
import {
  assertWrappedQueryRef,
  getWrappedPromise,
  InternalQueryReference,
  wrapQueryRef,
} from "@apollo/client/react/internal";
import type {
  NoInfer,
  OptionWithFallback,
  Prettify,
  SignatureStyle,
  VariablesOption,
} from "@apollo/client/utilities/internal";
import { FinalizationRegistry } from "@apollo/client/utilities/internal/ponyfills";

import { wrapHook } from "../hooks/internal/index.js";

export type PreloadQueryFetchPolicy = Extract<
  WatchQueryFetchPolicy,
  "cache-first" | "network-only" | "no-cache" | "cache-and-network"
>;

export type PreloadQueryOptions<
  TVariables extends OperationVariables = OperationVariables,
> = {
  /** {@inheritDoc @apollo/client!QueryOptionsDocumentation#context:member} */
  context?: DefaultContext;
  /** {@inheritDoc @apollo/client!QueryOptionsDocumentation#errorPolicy:member} */
  errorPolicy?: ErrorPolicy;
  /** {@inheritDoc @apollo/client!QueryOptionsDocumentation#fetchPolicy:member} */
  fetchPolicy?: PreloadQueryFetchPolicy;
  /** {@inheritDoc @apollo/client!QueryOptionsDocumentation#returnPartialData:member} */
  returnPartialData?: boolean;
  /** {@inheritDoc @apollo/client!QueryOptionsDocumentation#refetchWritePolicy:member} */
  refetchWritePolicy?: RefetchWritePolicy;
  /** {@inheritDoc @apollo/client!QueryOptionsDocumentation#refetchOn:member} */
  refetchOn?: RefetchOn.Option;
} & VariablesOption<TVariables>;

/**
 * A function that will begin loading a query when called. It's result can be
 * read by `useReadQuery` which will suspend until the query is loaded.
 * This is useful when you want to start loading a query as early as possible
 * outside of a React component.
 *
 * @example
 *
 * ```js
 * const preloadQuery = createQueryPreloader(client);
 * const queryRef = preloadQuery(query, { variables, ...otherOptions });
 *
 * function App() {
 *   return (
 *     <Suspense fallback={<div>Loading</div>}>
 *       <MyQuery />
 *     </Suspense>
 *   );
 * }
 *
 * function MyQuery() {
 *   const { data } = useReadQuery(queryRef);
 *
 *   // do something with `data`
 * }
 * ```
 */
export interface PreloadQueryFunction
  extends PreloadQueryFunction.Signatures.Evaluated {
  /**
   * A function that returns a promise that resolves when the query has finished
   * loading. The promise resolves with the `QueryReference` itself.
   *
   * @remarks
   * This method is useful for preloading queries in data loading routers, such
   * as [React Router](https://reactrouter.com/en/main) or [TanStack Router](https://tanstack.com/router),
   * to prevent routes from transitioning until the query has finished loading.
   * `data` is not exposed on the promise to discourage using the data in
   * `loader` functions and exposing it to your route components. Instead, we
   * prefer you rely on `useReadQuery` to access the data to ensure your
   * component can rerender with cache updates. If you need to access raw query
   * data, use `client.query()` directly.
   *
   * @example
   * Here's an example using React Router's `loader` function:
   *
   * ```ts
   * import { createQueryPreloader } from "@apollo/client";
   *
   * const preloadQuery = createQueryPreloader(client);
   *
   * export async function loader() {
   *   const queryRef = preloadQuery(GET_DOGS_QUERY);
   *
   *   return preloadQuery.toPromise(queryRef);
   * }
   *
   * export function RouteComponent() {
   *   const queryRef = useLoaderData();
   *   const { data } = useReadQuery(queryRef);
   *
   *   // ...
   * }
   * ```
   */
  toPromise<TQueryRef extends PreloadedQueryRef<any, any, any>>(
    queryRef: TQueryRef
  ): Promise<TQueryRef>;
}

export declare namespace PreloadQueryFunction {
  export interface DefaultOptions
    extends ApolloClient.DefaultOptions.WatchQuery.Calculated {}

  export namespace DocumentationTypes {
    /**
     * @deprecated Avoid manually specifying generics on `preloadQuery`.
     * Instead, rely on TypeScript's type inference along with a correctly typed `TypedDocumentNode` to get accurate types for your query results.
     *
     * {@inheritDoc @apollo/client/react!PreloadQueryFunction:interface}
     */
    export interface PreloadQueryFunction_Deprecated
      extends PreloadQueryFunction {}
  }

  export type OptionsFor<
    TVariables extends OperationVariables,
    TOptions extends { variables?: unknown },
  > = PreloadQueryOptions<NoInfer<TVariables>> & {
    variables?: Prettify<
      TVariables & {
        // variables that are not part of `TVariables`
        [K in keyof TOptions["variables"]]: K extends keyof TVariables ?
          TVariables[K]
        : never;
      }
    >;
  } & (Exclude<keyof TOptions, keyof PreloadQueryOptions<any>> extends (
      infer InvalidOptionNames extends string
    ) ?
      [InvalidOptionNames] extends [never] ?
        unknown
      : { [K in InvalidOptionNames]: never }
    : never);

  export type ResultForOptions<
    TData,
    TVariables extends OperationVariables,
    TOptions extends
      | Record<string, never>
      | Omit<PreloadQueryOptions<any>, "variables">,
  > = TOptions extends any ?
    PreloadedQueryRef<
      TData,
      TVariables,
      ResultForOptions.States<TOptions, DefaultOptions>
    >
  : never;

  export namespace ResultForOptions {
    export type States<
      TOptions,
      TDefaultOptions extends DefaultOptions = DefaultOptions,
    > =
      | "complete"
      | "streaming"
      | (OptionWithFallback<TOptions, TDefaultOptions, "errorPolicy"> extends (
          "none"
        ) ?
          never
        : "empty")
      | (OptionWithFallback<
          TOptions,
          TDefaultOptions,
          "returnPartialData"
        > extends false ?
          never
        : "partial");
  }

  export namespace Signatures {
    export interface Classic {
      // _INFERENCE_ONLY_DO_NOT_SPECIFY is used to distinguish between inferred
      // generics arguments and explicit generic arguments so that we can
      // provide a `@deprecated` signature for explicit generic arguments. As
      // soon as a user provides a generic arg (e.g. preloadQuery<TData>(query))`,
      // the overload falls through to the overloads without
      // _INFERENCE_ONLY_DO_NOT_SPECIFY.

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction:interface} */
      <
        TData,
        TVariables extends OperationVariables,
        _INFERENCE_ONLY_DO_NOT_SPECIFY extends "inferred",
      >(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options: PreloadQueryOptions<NoInfer<TVariables>> & {
          returnPartialData: true;
          errorPolicy: "ignore" | "all";
        }
      ): PreloadedQueryRef<
        TData,
        TVariables,
        "complete" | "streaming" | "partial" | "empty"
      >;

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction:interface} */
      <
        TData,
        TVariables extends OperationVariables,
        _INFERENCE_ONLY_DO_NOT_SPECIFY extends "inferred",
      >(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options: PreloadQueryOptions<NoInfer<TVariables>> & {
          errorPolicy: "ignore" | "all";
        }
      ): PreloadedQueryRef<
        TData,
        TVariables,
        "complete" | "streaming" | "empty"
      >;

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction:interface} */
      <
        TData,
        TVariables extends OperationVariables,
        _INFERENCE_ONLY_DO_NOT_SPECIFY extends "inferred",
      >(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options: PreloadQueryOptions<NoInfer<TVariables>> & {
          returnPartialData: true;
        }
      ): PreloadedQueryRef<
        TData,
        TVariables,
        "complete" | "streaming" | "partial"
      >;

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction:interface} */
      <
        TData,
        TVariables extends OperationVariables,
        _INFERENCE_ONLY_DO_NOT_SPECIFY extends "inferred",
      >(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        ...[options]: {} extends TVariables ?
          [options?: PreloadQueryOptions<NoInfer<TVariables>>]
        : [options: PreloadQueryOptions<NoInfer<TVariables>>]
      ): PreloadedQueryRef<TData, TVariables, "complete" | "streaming">;

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction.DocumentationTypes.PreloadQueryFunction_Deprecated:interface} */
      <TData, TVariables extends OperationVariables = OperationVariables>(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options: PreloadQueryOptions<NoInfer<TVariables>> & {
          returnPartialData: true;
          errorPolicy: "ignore" | "all";
        }
      ): PreloadedQueryRef<
        TData,
        TVariables,
        "complete" | "streaming" | "partial" | "empty"
      >;

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction.DocumentationTypes.PreloadQueryFunction_Deprecated:interface} */
      <TData, TVariables extends OperationVariables = OperationVariables>(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options: PreloadQueryOptions<NoInfer<TVariables>> & {
          errorPolicy: "ignore" | "all";
        }
      ): PreloadedQueryRef<
        TData,
        TVariables,
        "complete" | "streaming" | "empty"
      >;

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction.DocumentationTypes.PreloadQueryFunction_Deprecated:interface} */
      <TData, TVariables extends OperationVariables = OperationVariables>(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        options: PreloadQueryOptions<NoInfer<TVariables>> & {
          returnPartialData: true;
        }
      ): PreloadedQueryRef<
        TData,
        TVariables,
        "complete" | "streaming" | "partial"
      >;

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction.DocumentationTypes.PreloadQueryFunction_Deprecated:interface} */
      <TData, TVariables extends OperationVariables = OperationVariables>(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        ...[options]: {} extends TVariables ?
          [options?: PreloadQueryOptions<NoInfer<TVariables>>]
        : [options: PreloadQueryOptions<NoInfer<TVariables>>]
      ): PreloadedQueryRef<TData, TVariables, "complete" | "streaming">;
    }

    /** {@inheritDoc @apollo/client/react!PreloadQueryFunction:interface} */
    export interface Modern {
      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction:interface} */
      <
        TData,
        TVariables extends OperationVariables,
        // this overload should never be manually defined, it should always be inferred
        TOptions extends never,
      >(
        query: {} extends TVariables ?
          DocumentNode | TypedDocumentNode<TData, TVariables>
        : // this overload should only be accessible if all `TVariables` are optional
          never
      ): PreloadQueryFunction.ResultForOptions<
        TData,
        TVariables,
        Record<string, never>
      >;

      /** {@inheritDoc @apollo/client/react!PreloadQueryFunction:interface} */
      <
        TData,
        TVariables extends OperationVariables,
        // this overload should never be manually defined, it should always be inferred
        TOptions extends Omit<PreloadQueryOptions<any>, "variables"> & {
          variables?: unknown;
        },
      >(
        query: DocumentNode | TypedDocumentNode<TData, TVariables>,
        ...[options]: {} extends TVariables ?
          [
            options?: TOptions &
              PreloadQueryFunction.OptionsFor<TVariables, TOptions>,
          ]
        : [
            options: TOptions &
              PreloadQueryFunction.OptionsFor<TVariables, TOptions>,
          ]
      ): PreloadQueryFunction.ResultForOptions<TData, TVariables, TOptions>;
    }

    export type Evaluated = SignatureStyle extends "classic" ? Classic : Modern;
  }
}

/**
 * A higher order function that returns a `preloadQuery` function which
 * can be used to begin loading a query with the given `client`. This is useful
 * when you want to start loading a query as early as possible outside of a
 * React component.
 *
 * > Refer to the [Suspense - Initiating queries outside React](https://www.apollographql.com/docs/react/data/suspense#initiating-queries-outside-react) section for a more in-depth overview.
 *
 * @param client - The `ApolloClient` instance that will be used to load queries
 * from the returned `preloadQuery` function.
 * @returns The `preloadQuery` function.
 *
 * @example
 *
 * ```js
 * const preloadQuery = createQueryPreloader(client);
 * ```
 */
export function createQueryPreloader(
  client: ApolloClient
): PreloadQueryFunction {
  return wrapHook(
    "createQueryPreloader",
    _createQueryPreloader,
    client
  )(client);
}

const _createQueryPreloader: typeof createQueryPreloader = (client) => {
  function preloadQuery<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables,
  >(
    query: DocumentNode | TypedDocumentNode<TData, TVariables>,
    options: PreloadQueryOptions<NoInfer<TVariables>> &
      VariablesOption<TVariables> = {} as any
  ): PreloadedQueryRef<TData, TVariables> {
    const queryRef = new InternalQueryReference(
      client.watchQuery({
        ...options,
        query,
        notifyOnNetworkStatusChange: false,
      } as ApolloClient.WatchQueryOptions<any, any>),
      {
        autoDisposeTimeoutMs:
          client.defaultOptions.react?.suspense?.autoDisposeTimeoutMs,
      }
    );

    const wrapped = wrapQueryRef(queryRef) as unknown as PreloadedQueryRef<
      TData,
      TVariables
    >;
    softRetainWhileReferenced(wrapped, queryRef);
    return wrapped;
  }

  return Object.assign(preloadQuery, {
    toPromise<TQueryRef extends PreloadedQueryRef<any, any, any>>(
      queryRef: TQueryRef
    ) {
      assertWrappedQueryRef(queryRef);
      return getWrappedPromise(queryRef).then(() => queryRef);
    },
  });
};

/**
 * Soft-retains the underlying `InternalQueryReference` while the `PreloadedQueryRef`
 * is still reachable.
 * When the `PreloadedQueryRef` is garbage collected, the soft retain is
 * disposed of, but only after the initial query has finished loading.
 * Once the `InternalQueryReference` is properly retained, the check for garbage
 * collection is unregistered and the soft retain is disposed of immediately.
 */
// this is an individual function to avoid closing over any values more than necessary
function softRetainWhileReferenced(
  wrapped: PreloadedQueryRef<any, any, any>,
  queryRef: InternalQueryReference
) {
  const { softDispose, delayedSoftDispose } = getCleanup(queryRef);
  registry.register(wrapped, delayedSoftDispose, queryRef);
  // This will unregister the cleanup from the finalization registry when
  // the queryRef is properly retained.
  // This is mostly done to keep the FinalizationRegistry from holding too many
  // cleanup functions, as our React Native polyfill has to iterate all of them regularly.
  queryRef.retain = unregisterOnRetain(queryRef.retain, softDispose);
}

type RetainFunction = (
  this: InternalQueryReference,
  ...args: Parameters<InternalQueryReference["retain"]>
) => ReturnType<InternalQueryReference["retain"]>;

// this is an individual function to avoid closing over any values more than necessary
function unregisterOnRetain(
  originalRetain: RetainFunction,
  softDispose: () => void
): RetainFunction {
  return function (...args) {
    registry.unregister(this);
    const dispose = originalRetain.apply(this, args);
    softDispose();
    return dispose;
  };
}

// this is an individual function to avoid closing over any values more than necessary
function getCleanup(queryRef: InternalQueryReference) {
  const softDispose = queryRef.softRetain();
  const initialPromise = queryRef.promise;

  return {
    softDispose,
    delayedSoftDispose: () =>
      initialPromise.finally(softDispose).catch(() => {}),
  };
}

const registry = new FinalizationRegistry<() => void>((cleanup) => cleanup());
